import { Form, FormCancel } from "../components";
import { useFeedback } from "../hooks/useFeedback";
import PasswordInput from '../components/PasswordInput';
import Icon from "../components/Icon";
import { Toast, PageTitle, Card, FormSection, Btn, ConfirmModal, Input, Select, Modal, Spinner } from "../components";
import { passwordValidationError } from '../utils/passwordPolicy';
import { useState, useEffect } from "react";
import { apiFetch } from "../api";

export default function UsuariosPage({ userEmail, userRole, setUserName }) {
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ nome: "", email: "", senha: "", cliente_id: "" });
  const [submitError, setSubmitError] = useState("");
  const { toast, toastType, showToast, showError, dismissToast } = useFeedback("usuarios");
  const [confirmAction, setConfirmAction] = useState(null);

  const isSuperadmin = userRole === "superadmin";

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    const promises = [apiFetch("/usuarios")];
    if (isSuperadmin) promises.push(apiFetch("/clientes-rotaflex"));
    Promise.all(promises)
      .then(([u, c]) => { setUsuarios(u); if (c) setClientes(c); })
      .catch(e => { setLoadError(e.message); showError(e.message); })
      .finally(() => setLoading(false));
  }, [refresh, showError, isSuperadmin]);



  const openNew = () => {
    setSubmitError("");
    setForm({ nome: "", email: "", senha: "", cliente_id: "" });
    setEditItem(null);
    setModal(true);
  };

  const openEdit = (u) => {
    setSubmitError("");
    setForm({ nome: u.nome, email: u.email, senha: "", cliente_id: u.cliente_id || "" });
    setEditItem(u.id);
    setModal(true);
  };

  const save = async () => {
    if (saving) return;
    setSubmitError("");
    if (!form.nome.trim() || !form.email.trim()) { setSubmitError("Preencha nome e e-mail."); return; }
    if (!editItem && !form.senha) { setSubmitError("Preencha a senha do novo usuário."); return; }
    if (form.senha && passwordValidationError(form.senha)) { setSubmitError(passwordValidationError(form.senha)); return; }
    setSaving(true);
    try {
      if (editItem) {
        const body = { nome: form.nome, email: form.email, senha: form.senha };
        if (isSuperadmin) body.cliente_id = form.cliente_id || null;
        const res = await apiFetch(`/usuarios/${editItem}/dados`, { method: "PUT", body: JSON.stringify(body) });
        setUsuarios(usuarios.map(u => u.id === editItem ? { ...u, ...res } : u));
        if (res.email === userEmail) setUserName(res.nome);
        showToast("Usuário atualizado.");
      } else {
        const body = { nome: form.nome, email: form.email, senha: form.senha, role: "operador" };
        if (isSuperadmin) body.cliente_id = form.cliente_id || null;
        const res = await apiFetch("/usuarios", { method: "POST", body: JSON.stringify(body) });
        const clienteNome = clientes.find(c => c.id === res.cliente_id)?.nome || null;
        setUsuarios([...usuarios, { ...res, cliente_nome: clienteNome }]);
        showToast("Usuário cadastrado.");
      }
      setModal(false);
    } catch (e) { setSubmitError(e.message); showError(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    try {
      await apiFetch(`/usuarios/${u.id}`, { method: "DELETE" });
      setUsuarios(usuarios.filter(x => x.id !== u.id));
      showToast("Usuário excluído.");
    } catch (e) { showError(e.message); }
  };

  const toggleAtivo = async (u) => {
    try {
      await apiFetch(`/usuarios/${u.id}`, { method: "PUT", body: JSON.stringify({ ativo: !u.ativo }) });
      setUsuarios(usuarios.map(x => x.id === u.id ? { ...x, ativo: !x.ativo } : x));
      showToast(`Usuário ${u.ativo ? "desativado" : "reativado"}.`);
    } catch (e) { showError(e.message); }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("pt-BR");

  // Colunas variam de acordo com o nível de acesso
  const headers = isSuperadmin
    ? ["Nome", "E-mail", "Cliente", "Cadastrado em", "Status", ""]
    : ["Nome", "E-mail", "Cadastrado em", "Status", ""];
  const colSpan = headers.length;

  return (
    <div className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <div>
          <PageTitle icon="shield">Usuários</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 2 }}>{usuarios.length} usuário{usuarios.length === 1 ? "" : "s"} cadastrado{usuarios.length === 1 ? "" : "s"}</p>
        </div>
        <Btn onClick={openNew}>+ Novo usuário</Btn>
      </div>

      {loadError && <Card style={{ marginBottom: 16 }}><p role="alert" style={{ color: "var(--danger)", marginBottom: 12 }}>{loadError}</p><Btn variant="secondary" onClick={() => setRefresh(value => value + 1)}>Tentar novamente</Btn></Card>}

      <Card style={{ padding: 0, overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: 680, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {headers.map(h => (
                <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={colSpan} style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}><Spinner /></td></tr>}
            {!loading && !loadError && usuarios.length === 0 && (
              <tr><td colSpan={colSpan} style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Nenhum usuário cadastrado</td></tr>
            )}
            {!loading && usuarios.map((u, i) => (
              <tr key={u.id}
                style={{ borderBottom: i < usuarios.length - 1 ? "1px solid var(--border)" : "none", transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "12px 16px", fontWeight: 500 }}>{u.nome}</td>
                <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem', fontFamily: "var(--font-mono)" }}>{u.email}</td>
                {isSuperadmin && (
                  <td style={{ padding: "12px 16px", fontSize: '0.9375rem' }}>
                    {u.cliente_nome
                      ? <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 6, background: "rgba(249,115,22,0.1)", color: "var(--accent)", fontSize: '0.875rem', fontWeight: 600 }}><Icon name="building" /> {u.cliente_nome}</span>
                      : <span style={{ color: "var(--muted)", fontSize: '0.875rem', fontStyle: "italic" }}>superadmin</span>
                    }
                  </td>
                )}
                <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>{formatDate(u.criado_em)}</td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: '0.8125rem', fontWeight: 600, background: u.ativo ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)", color: u.ativo ? "#22c55e" : "#ef4444" }}>
                    {u.ativo ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn size="sm" variant="secondary" onClick={() => openEdit(u)}>Editar</Btn>
                    {u.email !== userEmail && (
                      <>
                        <Btn size="sm" variant={u.ativo ? "danger" : "ghost"} onClick={() => setConfirmAction({ type: "toggleAtivo", user: u })}>
                          {u.ativo ? "Desativar" : "Reativar"}
                        </Btn>
                        <Btn size="sm" variant="danger" onClick={() => setConfirmAction({ type: "remove", user: u })}>Excluir</Btn>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {modal && (
        <Modal title={editItem ? "Editar usuário" : "Novo usuário"} onClose={() => !saving && setModal(false)}>
          <Form values={form} onSubmit={save} busy={saving} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ color: "var(--muted)", fontSize: '0.875rem' }}>Campos com * são obrigatórios.</p>
            <FormSection title="Identificação">
            <Input label="Nome completo *" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="João da Silva" />
            <Input label="E-mail *" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="joao@email.com" />
            </FormSection>
            <FormSection title="Acesso ao sistema">
            {editItem && usuarios.find(u => u.id === editItem)?.email === userEmail ? <p style={{ color: "var(--muted)", fontSize: '0.9375rem' }}>Para trocar sua senha com segurança, use “Alterar minha senha” no menu.</p> : <PasswordInput
              label={editItem ? "Nova senha" : "Senha *"}
              hint={editItem ? "Deixe em branco para manter a senha atual." : "Use pelo menos 8 caracteres."}
              autoComplete="new-password" minLength={8}
              value={form.senha}
              onChange={e => setForm({ ...form, senha: e.target.value })}
            />}

            {isSuperadmin && (
              <Select
                label="Transportadora"
                value={form.cliente_id}
                onChange={e => setForm({ ...form, cliente_id: e.target.value })}
              >
                <option value="">— Sem cliente (superadmin) —</option>
                {clientes.filter(c => c.ativo).map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </Select>
            )}

            </FormSection>
            {submitError && <p role="alert" style={{ color: "var(--danger)", fontSize: '0.9375rem', lineHeight: 1.5 }}>{submitError}</p>}
            <div className="rf-form-actions">
              <FormCancel disabled={saving} />
              <Btn type="submit" disabled={saving} >{saving ? <><Spinner /> Salvando...</> : "Salvar usuário"}</Btn>
            </div>
          </Form>
        </Modal>
      )}

      <ConfirmModal
        open={Boolean(confirmAction)}
        title={confirmAction?.type === "remove" ? "Excluir usuário" : `${confirmAction?.user?.ativo ? "Desativar" : "Reativar"} usuário`}
        message={confirmAction?.type === "remove"
          ? `Deseja realmente excluir o usuário ${confirmAction?.user?.nome}?`
          : `Deseja ${confirmAction?.user?.ativo ? "desativar" : "reativar"} o usuário ${confirmAction?.user?.nome}?`}
        confirmLabel={confirmAction?.type === "remove"
          ? "Excluir"
          : (confirmAction?.user?.ativo ? "Desativar" : "Reativar")}
        confirmVariant={confirmAction?.type === "remove" || confirmAction?.user?.ativo ? "danger" : "primary"}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => {
          const current = confirmAction;
          if (!current?.user) return;
          if (current.type === "remove") {
            await remove(current.user);
          } else {
            await toggleAtivo(current.user);
          }
          setConfirmAction(null);
        }}
      />

      {toast && !(modal && submitError) && (
        <Toast onDismiss={dismissToast} tone={toastType === "error" ? "error" : "success"}>{toast}</Toast>
      )}
    </div>
  );
}
